// src/components/ui/SearchSortInput.js
import React, { useState } from 'react';
import { Search, ArrowDownAZ, ClockArrowDown, CalendarArrowDown } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent } from './Tooltip';

const SearchSortInput = ({ 
  searchTerm = '', 
  onSearchChange, 
  sortBy = 'modified', 
  onSortChange,
  collapsed = false 
}) => {
  // Sort options matching the existing CasesSidebar values
  const sortOptions = {
    modified: { icon: ClockArrowDown, label: 'Last Modified' },
    alphabetical: { icon: ArrowDownAZ, label: 'Alphabetical' },
    created: { icon: CalendarArrowDown, label: 'Date Created' }
  };

  const sortOrder = ['modified', 'alphabetical', 'created'];

  // State for managing fade animation
  const [isAnimating, setIsAnimating] = useState(false);
  
  // State for hover/focus effects on search input
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Don't render when collapsed
  if (collapsed) return null;

  const handleCycleSort = () => {
    if (isAnimating) return; // Prevent rapid clicking during animation
    
    setIsAnimating(true);
    
    // After fade out completes, change the sort option
    setTimeout(() => {
      const currentIndex = sortOrder.indexOf(sortBy);
      const nextIndex = (currentIndex + 1) % sortOrder.length;
      const nextSort = sortOrder[nextIndex];
      onSortChange?.(nextSort);
      
      // After sort changes, fade back in
      setTimeout(() => {
        setIsAnimating(false);
      }, 150); // Fade in duration
    }, 150); // Fade out duration
  };

  return (
    <div className="mb-app-md">
      {/* Main input group container - combines search with hover/focus and sort button */}
      <div className={`w-full flex items-center bg-app-white rounded-app-sm transition-all duration-app-normal overflow-hiddenring ring-app-gray-900 ${
          isFocused ? 'ring-2' : 'ring-1'
      }`}
      >
        {/* Search section with hover/focus effects */}
        <div 
          className="flex-1 min-w-0 relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Background layer - only for search section with rounded corners on both sides */}
          <div 
            className={`
              absolute bg-app-gray-300
              ${isFocused ? 'transition-all duration-app-normal' : ''}
              ${isFocused 
                ? 'inset-0 rounded-app-sm mr-[3px]' 
                : isHovered
                  ? 'inset-[3px] rounded-app-sm' 
                  : 'inset-0 opacity-0 rounded-app-sm'
              }
            `}
          />
          
          {/* Search input content */}
          <div className="relative z-10 flex items-center px-app-sm h-[32px]">
            <Search 
              size={14}
              strokeWidth={2}
              className="mr-app-sm mb-[2px] flex-shrink-0"
              style={{
                color: isHovered || isFocused ? '#8F99A3' : '#C0C7CE'
              }}
            />
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => onSearchChange?.(e.target.value)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className={`
                flex-1 min-w-0 bg-transparent border-none outline-none 
                text-app-gray-900 text-app-sm font-normal
                placeholder:font-normal
                transition-colors duration-app-normal
                ${isHovered || isFocused 
                  ? 'placeholder:text-app-gray-500' 
                  : 'placeholder:text-app-gray-400'
                }
              `}
            />
          </div>
        </div>

        {/* Vertical divider */}
        <div className="w-px h-5 bg-app-gray-900" />

        {/* Sort button with tooltip */}
        <div className="relative">
          <Tooltip>
            <TooltipTrigger asChild>
              <button 
                onClick={handleCycleSort}
                className="relative flex items-center justify-center px-app-sm py-app-xs transition-all duration-200 cursor-pointer flex-shrink-0 w-8 group"
                aria-label={`Sort by ${sortOptions[sortBy].label}`}
                disabled={isAnimating}
              >
                {/* Icon with fade transition */}
                <div
                  className={`transition-opacity duration-150 ease-in-out ${
                    isAnimating ? 'opacity-0' : 'opacity-100'
                  }`}
                >
                  {(() => {
                    const IconComponent = sortOptions[sortBy].icon;
                    return (
                      <IconComponent 
                        className="text-app-gray-900 transition-all duration-200 hover:scale-110"
                        size={14}
                        strokeWidth={2}
                      />
                    );
                  })()}
                </div>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Sort by {sortOptions[sortBy].label}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

export default SearchSortInput;